import { memo } from 'react';
import { capitalize } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { Text, Tooltip, Icon } from '@hotjar/hotsauce';
import { COUNTRIES, CountryFlag } from '@hotjar/country-lib';
import { useCreateTimeAgoLabelFromRecording } from '../../../hooks/useCreateTimeAgoLabelFromRecording';
import { transformBrowserNameToIcon } from '../../../utils/transformBrowserNameToIcon';

import type { Recording } from '@hotjar/webapp/domains/recordings/types';
import type { IconName } from '@hotjar/hotsauce';

import { RecordingTileMain } from './RecordingTileMain';
import {
  StyledRecordingTileContainer,
  StyledRecordingTileFlag,
  StyledRecordingTileFooter,
  StyledRecordingTileHeader,
  StyledRecordingTileHeaderIcons,
  StyleRecordingTileDuration,
} from './styles';
import { getMinutesAndSeconds } from '@hotjar/insights-common/lib/getMinutesAndSeconds';

type RecordingTileParams = {
  recording: Recording;
  onClick: () => void;
};

const UnMemoRecordingTile = ({ recording, onClick }: RecordingTileParams): JSX.Element => {
  const { t } = useTranslation();

  const timeAgoLabel = useCreateTimeAgoLabelFromRecording(recording);
  const deviceTooltipText = `${capitalize(recording.device)} (${recording.windowSize[0]} X ${
    recording.windowSize[1]
  })`;
  const deviceIconName = `device_${recording.device}` as IconName;
  const countryName =
    COUNTRIES.find((country) => country.value === recording.countryCode)?.label ||
    t('common:recordings.recording-tile.country');

  return (
    <StyledRecordingTileContainer onClick={onClick} data-testid="recording-tile">
      <StyledRecordingTileHeader>
        <Text textType="subtitle2">
          {recording.isFirstSeen
            ? t('common:recordings.recording-tile.new-user')
            : t('common:recordings.recording-tile.returning-user')}
        </Text>
        <StyledRecordingTileHeaderIcons data-testid="recording-tile-icons">
          <Tooltip text={deviceTooltipText} placement="top" appendTo="parent">
            <Icon icon={deviceIconName} size="m" />
          </Tooltip>
          <Tooltip text={countryName}>
            <StyledRecordingTileFlag>
              <CountryFlag code={recording.countryCode} />
            </StyledRecordingTileFlag>
          </Tooltip>
          <Tooltip text={recording.browser} placement="top" appendTo="parent">
            <Icon icon={transformBrowserNameToIcon(recording.browser)} size="m" />
          </Tooltip>
        </StyledRecordingTileHeaderIcons>
        <Text textType="caption" textColor="text_light">
          {timeAgoLabel}
        </Text>
        <StyleRecordingTileDuration textType="caption" textColor="text_light">
          {`${getMinutesAndSeconds(recording.duration)} min`}
        </StyleRecordingTileDuration>
      </StyledRecordingTileHeader>
      <RecordingTileMain recording={recording}></RecordingTileMain>
      <StyledRecordingTileFooter>
        <Icon icon="play_alt" />
        <Text textType="subtitle2" textColor="inherited">
          {t('common:recordings.recording-tile.play')}
        </Text>
      </StyledRecordingTileFooter>
    </StyledRecordingTileContainer>
  );
};

export const RecordingTile = memo(UnMemoRecordingTile);
